import { Injectable } from '@angular/core';
import { Chat } from 'common/xislivechat/models/chat';
import { BcRestApi } from 'common/api/rest-api';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { VideoSessionStatus } from 'common/common/constants/video-session-status';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { VendorVideoSessionResponse } from 'common/api/models/vendor-video-session-response.model';
import { PopupWindowService } from 'shared/services/popup-window/popup-window.service';

export const VIDEO_WINDOW_TARGET = 'bold360_video';

@Injectable({
  providedIn: 'root'
})
export class VideoService {
  private _videoUrl: string;
  private _chat: Chat = null;
  private _chatSubject = new BehaviorSubject<Chat>(this._chat);
  private _activeChatsSubscription: Subscription;
  private _workItemRemovalSubscription: Subscription;

  constructor(
    private _popupWindowService: PopupWindowService,
    private _bcRestApi: BcRestApi,
    private _workItemListenerService: WorkItemListenerService
  ) {
  }

  initSubscriptions(): void {
    // TODO solve multiple chats
    this._activeChatsSubscription = this._workItemListenerService.observeActiveChatsForVideoSessionStatus().subscribe(chat => {
      // if the agent isn't in an active call, and there's an incoming chat what was initiated or accepted by either party
      // we are saving the chat
      if (!this._chat && VideoSessionStatus.isInSession(chat.VideoSessionStatus)) {
        this._setChat(chat);
      }
      // if the agent is in an active call, and the call was ended or declined by either party
      // we are clearing the chat, or for other status changes keeping it
      if (this._chat && this._chat.ChatID === chat.ChatID) {
        this._setChat(!VideoSessionStatus.isInSession(chat.VideoSessionStatus) ? null : chat);
      }
    });

    this._workItemRemovalSubscription = this._workItemListenerService.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT)
      .subscribe(workItem => {
        if (this._chat && workItem.id === this._chat.ChatID) {
          this._clearChat();
        }
    });
  }

  clearSubscriptions(): void {
    if (this._activeChatsSubscription) {
      this._activeChatsSubscription.unsubscribe();
    }

    if (this._workItemRemovalSubscription) {
      this._workItemRemovalSubscription.unsubscribe();
    }
  }

  async startVideoCall(chat: Chat, enforced = false, autoStart = false): Promise<boolean> {
    this._popupWindowService.preparePopupFor(VIDEO_WINDOW_TARGET);

    const { success, agentUrl }: VendorVideoSessionResponse =
      await this._bcRestApi.startVendorVideoSession(chat.ChatID, enforced, autoStart);

    if (success) {
      this._setChat(chat);
      this._videoUrl = agentUrl;
      this._popupWindowService.navigateAndFocusOrOpenPopupWindow(this._videoUrl, VIDEO_WINDOW_TARGET);
    }
    return success;
  }

  async focusOrOpenVideoWindow(chat: Chat): Promise<void> {
    if (chat.ChatID !== this._chat.ChatID) {
      return;
    }

    this._popupWindowService.preparePopupFor(VIDEO_WINDOW_TARGET);

    const hasValidUrl = await this._ensureVideoUrl();
    if (!hasValidUrl) {
      return;
    }

    this._popupWindowService.focusOrOpenPopupWindow(this._videoUrl, VIDEO_WINDOW_TARGET);
  }

  observeActiveChat(): Observable<Chat> {
    return this._chatSubject.asObservable();
  }

  private async _ensureVideoUrl() {
    if (this._videoUrl) {
      return true;
    }

    const videoSession = await this._bcRestApi.getVendorVideoSession(this._chat.ChatID);

    if (!videoSession.success) {
      return false;
    }

    this._videoUrl = videoSession.agentUrl;
    return true;
  }

  private _setChat(chat: Chat): void {
    this._chat = chat;
    this._emitChat();
  }

  private _clearChat(): void {
    this._chat = null;
    this._emitChat();
  }

  private _emitChat(): void {
    this._chatSubject.next(this._chat);
  }
}
